import { useState } from 'react';
import { Property } from '@/mocks/properties';

interface BookingModalProps {
  property: Property;
  onClose: () => void;
}

const timeSlots = ['9:00 AM', '10:30 AM', '12:00 PM', '2:00 PM', '3:30 PM', '5:00 PM'];

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

const BookingModal = ({ property, onClose }: BookingModalProps) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [contactPref, setContactPref] = useState('email');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const isDisabled = (day: number) => {
    const d = new Date(currentYear, currentMonth, day);
    return d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const handleConfirm = () => {
    if (!selectedDate || !selectedTime || !name || !email) return;
    setConfirmed(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {confirmed ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-black text-stone-900 mb-2">Viewing Confirmed!</h3>
            <p className="text-stone-500 text-sm mb-1">
              <strong>{monthNames[currentMonth]} {selectedDate}, {currentYear}</strong> at <strong>{selectedTime}</strong>
            </p>
            <p className="text-stone-400 text-xs mb-6">{property.title}</p>
            <p className="text-stone-500 text-sm mb-6">
              A confirmation has been sent to <strong>{email}</strong>. Our agent will be in touch shortly.
            </p>
            <button
              onClick={onClose}
              className="px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div>
                <h3 className="font-black text-stone-900 text-base">Book a Viewing</h3>
                <p className="text-stone-400 text-xs mt-0.5 line-clamp-1">{property.title}</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-500 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Property Summary */}
              <div className="flex gap-3 bg-stone-50 rounded-xl p-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={property.image} alt={property.title} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-semibold text-stone-800 text-sm line-clamp-1">{property.title}</p>
                  <p className="text-stone-400 text-xs mt-0.5">{property.location}</p>
                  <p className="text-amber-600 font-bold text-sm mt-1">
                    ${property.price.toLocaleString()}{property.priceType === 'rent' ? '/mo' : ''}
                  </p>
                </div>
              </div>

              {/* Calendar */}
              <div>
                <p className="text-stone-700 font-semibold text-sm mb-3">Select Date</p>
                <div className="border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={prevMonth} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
                      <i className="ri-arrow-left-s-line text-stone-600"></i>
                    </button>
                    <span className="font-semibold text-stone-800 text-sm">{monthNames[currentMonth]} {currentYear}</span>
                    <button onClick={nextMonth} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-stone-600"></i>
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                      <div key={d} className="text-center text-xs text-stone-400 font-medium py-1">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const disabled = isDisabled(day);
                      const selected = selectedDate === day;
                      return (
                        <button
                          key={day}
                          disabled={disabled}
                          onClick={() => setSelectedDate(day)}
                          className={`w-full aspect-square flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                            selected
                              ? 'bg-amber-500 text-white'
                              : disabled
                              ? 'text-stone-300 cursor-not-allowed'
                              : 'hover:bg-amber-50 text-stone-700'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <p className="text-stone-700 font-semibold text-sm mb-3">Select Time</p>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map(slot => (
                    <button
                      key={slot}
                      onClick={() => setSelectedTime(slot)}
                      className={`py-2 rounded-xl text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap ${
                        selectedTime === slot
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'border-stone-200 text-stone-600 hover:border-amber-300'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Preference */}
              <div>
                <p className="text-stone-700 font-semibold text-sm mb-3">Contact Preference</p>
                <div className="flex gap-2">
                  {[
                    { value: 'email', icon: 'ri-mail-line', label: 'Email' },
                    { value: 'phone', icon: 'ri-phone-line', label: 'Phone' },
                    { value: 'whatsapp', icon: 'ri-whatsapp-line', label: 'WhatsApp' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setContactPref(opt.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap ${
                        contactPref === opt.value
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'border-stone-200 text-stone-600 hover:border-amber-300'
                      }`}
                    >
                      <i className={opt.icon}></i> {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <p className="text-stone-700 font-semibold text-sm">Your Details</p>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Full Name *"
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 text-stone-700"
                />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email Address *"
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 text-stone-700"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="Phone Number"
                  className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 text-stone-700"
                />
              </div>

              {/* Confirm Button */}
              <button
                onClick={handleConfirm}
                disabled={!selectedDate || !selectedTime || !name || !email}
                className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:bg-stone-200 disabled:text-stone-400 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Confirm Viewing
              </button>
              <button className="w-full py-3 border border-stone-200 text-stone-600 hover:border-stone-300 font-semibold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap">
                Request Virtual Tour Instead
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
